import { useEffect, useMemo, useState } from "@wordpress/element";

const BEER_ANIMATIONS = [
    "champagne_shower",
    "foam_cannon",
    "cheers",
    "beer_wave",
    "keg_drop",
    "hoppy_hour",
];

const BEER_EFFECTS = {
    champagne_shower: {
        label: "Locker-room beer shower",
        effect: "beer-rain",
        symbols: ["🍺", "🍻", "✨", "🏆"],
    },
    foam_cannon: {
        label: "Foam cannon",
        effect: "foam-rise",
        symbols: ["◯", "○", "·", "🍺"],
    },
    cheers: {
        label: "Big cheers",
        effect: "party-burst",
        symbols: ["🍺", "🍻", "💥", "✨"],
    },
    beer_wave: {
        label: "Stadium beer wave",
        effect: "stadium-wave",
        symbols: ["🍺", "🍻"],
    },
    keg_drop: {
        label: "Keg drop",
        effect: "keg-drop",
        symbols: ["🍺", "🛢️", "💥", "⭐"],
    },
    hoppy_hour: {
        label: "Hoppy hour",
        effect: "hop-rise",
        symbols: ["🌿", "🍺", "✨", "🌱"],
    },
};

export const ACHIEVEMENTS = {
    loser_of_week: {
        label: "Loser of the Week",
        icon: "🗑️",
        tagline: "Rough week. Historic vibes.",
        effect: "trash-rain",
        symbols: ["🗑️", "🥴", "📉", "🧻"],
        accent: "#5b6472",
        edge: "#2f3640",
        panelFrom: "#f0f2f5",
        panelTo: "#7f8c9a",
        ink: "#18202a",
    },
    toilet_bowl: {
        label: "Toilet Bowl Royalty",
        icon: "🚽",
        tagline: "Flushed, but never forgotten.",
        effect: "flush-spin",
        symbols: ["🚽", "🧻", "💧", "👑"],
        accent: "#42a5c6",
        edge: "#14758f",
        panelFrom: "#e8fbff",
        panelTo: "#62c9df",
        ink: "#073945",
    },
    dumpster_fire: {
        label: "Certified Dumpster Fire",
        icon: "🔥",
        tagline: "Everything was fine. Until it wasn't.",
        effect: "inferno-rise",
        symbols: ["🔥", "🗑️", "💥", "🌋"],
        accent: "#f04b23",
        edge: "#9e260d",
        panelFrom: "#fff0c7",
        panelTo: "#f15a24",
        ink: "#451205",
    },
    choke_artist: {
        label: "Choke Artist",
        icon: "😵",
        tagline: "A masterpiece in falling apart.",
        effect: "choke-tumble",
        symbols: ["😵", "💨", "📉", "🫠"],
        accent: "#a15fd1",
        edge: "#63308b",
        panelFrom: "#f4e5ff",
        panelTo: "#b66ce2",
        ink: "#321044",
    },
    lucky_duck: {
        label: "Lucky Duck",
        icon: "🦆",
        tagline: "Wins are wins. No questions.",
        effect: "clover-burst",
        symbols: ["🦆", "🍀", "✨", "🎲"],
        accent: "#24a45a",
        edge: "#126b37",
        panelFrom: "#e9ffd9",
        panelTo: "#65c96e",
        ink: "#123a20",
    },
    giant_killer: {
        label: "Giant Killer",
        icon: "🪓",
        tagline: "The favorite has left the chat.",
        effect: "axe-drop",
        symbols: ["🪓", "💥", "⭐", "🏆"],
        accent: "#c53a34",
        edge: "#7f1d1d",
        panelFrom: "#ffe8d4",
        panelTo: "#d95843",
        ink: "#42120d",
    },
    hot_streak: {
        label: "Hot Streak",
        icon: "🚀",
        tagline: "Absolutely cooking.",
        effect: "rocket-rise",
        symbols: ["🚀", "🔥", "⭐", "💨"],
        accent: "#f05b2a",
        edge: "#a93010",
        panelFrom: "#fff4b8",
        panelTo: "#ff6b35",
        ink: "#4b1908",
    },
    comeback_kid: {
        label: "Comeback Kid",
        icon: "🪃",
        tagline: "Never count this one out.",
        effect: "boomerang-loop",
        symbols: ["🪃", "↩️", "⚡", "✨"],
        accent: "#2374c6",
        edge: "#124a85",
        panelFrom: "#e4f4ff",
        panelTo: "#4b91dd",
        ink: "#082c52",
    },
    nail_biter: {
        label: "Nail-Biter Specialist",
        icon: "😬",
        tagline: "Making easy wins unnecessarily stressful.",
        effect: "nervous-rattle",
        symbols: ["😬", "⏱️", "💓", "❗"],
        accent: "#e39a19",
        edge: "#9a6105",
        panelFrom: "#fff8c8",
        panelTo: "#f1b238",
        ink: "#462e05",
    },
    stat_wizard: {
        label: "Stat Wizard",
        icon: "🧙",
        tagline: "The numbers have been successfully enchanted.",
        effect: "magic-orbit",
        symbols: ["🧙", "✨", "🔮", "📊"],
        accent: "#6846c7",
        edge: "#3c2588",
        panelFrom: "#eee8ff",
        panelTo: "#8064d9",
        ink: "#24144f",
    },
    sad_bears: {
        label: "Sad Bears",
        headline: "This Week's Sad Bears",
        icon: "🐻",
        tagline: "The thumbs-up heard 'round the NFC North.",
        effect: "sad-rain",
        symbols: ["🐻", "😭", "☔", "🩹"],
        image: "assets/images/sad-bears-caleb-cart.jpg",
        imageAlt: "Illustration of Caleb Williams giving a solemn thumbs-up while riding a medical cart through a rainy stadium",
        accent: "#c83803",
        edge: "#071d49",
        panelFrom: "#102c58",
        panelTo: "#c83803",
        ink: "#ffffff",
    },
    sad_vikings: {
        label: "Sad Vikings",
        headline: "The Longboat Has Sunk",
        icon: "🛡️",
        tagline: "Valhalla is closed for maintenance.",
        effect: "purple-tears",
        symbols: ["🛡️", "😭", "❄️", "⚔️"],
        accent: "#6b3fa0",
        edge: "#3b1d62",
        panelFrom: "#f4e6ff",
        panelTo: "#6b3fa0",
        ink: "#281039",
    },
    sad_packers: {
        label: "Sad Packers",
        headline: "Sad Cheese Noises",
        icon: "🧀",
        tagline: "The cheese has officially gone bad.",
        effect: "cheese-rain",
        symbols: ["🧀", "😭", "📉", "❄️"],
        accent: "#f5b700",
        edge: "#173f2b",
        panelFrom: "#fff5b8",
        panelTo: "#f5b700",
        ink: "#173f2b",
    },
};

const BEER_DEFINITION = {
    id: "beer",
    label: "Beer achievement",
    headline: "Beer achievement unlocked!",
    icon: "🍺",
    accent: "#f3a712",
    edge: "#a65c00",
    panelFrom: "#fff8d9",
    panelTo: "#e98b00",
    ink: "#3b2100",
};

const pickBeerAnimation = (animation) => {
    if (BEER_ANIMATIONS.includes(animation)) {
        return animation;
    }
    return BEER_ANIMATIONS[Math.floor(Math.random() * BEER_ANIMATIONS.length)];
};

const getAssetUrl = (path) => {
    const base =
        typeof wl !== "undefined" && wl.plugin_url
            ? wl.plugin_url
            : "/wp-content/plugins/wins-league/";
    return `${base.replace(/\/?$/, "/")}${path.replace(/^\//, "")}`;
};

const getParticle = (definition, index) => ({
    id: index,
    symbol: definition.symbols[index % definition.symbols.length],
    x: (index * 37 + 11) % 100,
    y: (index * 53 + 17) % 100,
    delay: (index % 8) * 0.08,
    duration: 1.4 + (index % 5) * 0.16,
    drift: ((index % 7) - 3) * 18,
    burstX: ((index % 7) - 3) * 144,
    burstY: ((index % 5) - 2) * 126,
    size: 18 + (index % 4) * 8,
});

const AchievementCelebration = ({ definition, onDone }) => {
    const pieces = useMemo(
        () => Array.from({ length: 30 }, (_, index) => getParticle(definition, index)),
        [definition]
    );

    useEffect(() => {
        const timer = window.setTimeout(onDone, 3800);
        return () => window.clearTimeout(timer);
    }, [onDone]);

    useEffect(() => {
        const closeOnEscape = (event) => {
            if (event.key === "Escape") {
                onDone();
            }
        };
        window.addEventListener("keydown", closeOnEscape);
        return () => window.removeEventListener("keydown", closeOnEscape);
    }, [onDone]);

    const style = {
        "--wl-achievement-accent": definition.accent,
        "--wl-achievement-edge": definition.edge,
        "--wl-achievement-panel-from": definition.panelFrom,
        "--wl-achievement-panel-to": definition.panelTo,
        "--wl-achievement-ink": definition.ink,
    };

    return (
        <div
            className={`wl-achievement-celebration wl-achievement-celebration--${definition.effect}`}
            style={style}
            role="status"
            aria-live="polite"
            onClick={onDone}
        >
            <div className="wl-achievement-celebration__scrim" />
            <div
                className={`wl-achievement-celebration__headline${
                    definition.image ? " wl-achievement-celebration__headline--image" : ""
                }`}
            >
                {definition.image ? (
                    <>
                        <img
                            className="wl-achievement-celebration__image"
                            src={getAssetUrl(definition.image)}
                            alt={definition.imageAlt || ""}
                        />
                        <div className="wl-achievement-celebration__image-copy">
                            <strong>{definition.headline || definition.label}</strong>
                            <span>{definition.tagline}</span>
                            <small>Click anywhere or press Esc to close</small>
                        </div>
                    </>
                ) : (
                    <>
                        <span className="wl-achievement-celebration__hero" aria-hidden="true">
                            {definition.icon}
                        </span>
                        <strong>{definition.headline || definition.label}</strong>
                        <span>{definition.tagline}</span>
                        <small>Click anywhere or press Esc to close</small>
                    </>
                )}
            </div>
            <div className="wl-achievement-celebration__particles" aria-hidden="true">
                {pieces.map((piece) => (
                    <span
                        key={piece.id}
                        className="wl-achievement-celebration__particle"
                        style={{
                            "--wl-x": `${piece.x}vw`,
                            "--wl-y": `${piece.y}vh`,
                            "--wl-delay": `${piece.delay}s`,
                            "--wl-duration": `${piece.duration}s`,
                            "--wl-drift": `${piece.drift}px`,
                            "--wl-burst-x": `${piece.burstX}px`,
                            "--wl-burst-y": `${piece.burstY}px`,
                            "--wl-size": `${piece.size}px`,
                        }}
                    >
                        {piece.symbol}
                    </span>
                ))}
            </div>
        </div>
    );
};

const AchievementBadges = ({ beerEnabled, beerAnimation, achievements, name }) => {
    const [activeAchievement, setActiveAchievement] = useState(null);
    const selected = Array.isArray(achievements)
        ? achievements.filter((id) => ACHIEVEMENTS[id])
        : [];
    const badges = selected.map((id) => ({ id, ...ACHIEVEMENTS[id] }));

    if (beerEnabled) {
        badges.unshift(BEER_DEFINITION);
    }

    if (!badges.length) {
        return null;
    }

    const launch = (badge) => {
        if (badge.id === "beer") {
            const animation = pickBeerAnimation(beerAnimation);
            setActiveAchievement({
                ...badge,
                tagline: BEER_EFFECTS[animation].label,
                effect: BEER_EFFECTS[animation].effect,
                symbols: BEER_EFFECTS[animation].symbols,
            });
            return;
        }
        setActiveAchievement(badge);
    };

    return (
        <>
            <span className="wl-achievement-list" aria-label={`${name}'s achievements`}>
                {badges.map((badge) => (
                    <button
                        key={badge.id}
                        type="button"
                        className={`wl-achievement-badge wl-achievement-badge--${badge.id}`}
                        style={{
                            "--wl-achievement-accent": badge.accent,
                            "--wl-achievement-edge": badge.edge,
                        }}
                        aria-label={`Open ${name}'s ${badge.label} celebration`}
                        title={`${badge.label} — click to reveal`}
                        onClick={() => launch(badge)}
                    >
                        <span aria-hidden="true">{badge.icon}</span>
                        <span className="wl-achievement-badge__spark" aria-hidden="true">
                            ✦
                        </span>
                    </button>
                ))}
            </span>
            {activeAchievement ? (
                <AchievementCelebration
                    definition={activeAchievement}
                    onDone={() => setActiveAchievement(null)}
                />
            ) : null}
        </>
    );
};

export default AchievementBadges;
